/**
 * The single source for mockd's colour and shape tokens (docs/brand.md → Themes).
 *
 * `packages/web/src/index.css` declares the same values as daisyUI themes, and
 * `brandTheme.test.ts` fails if the two disagree. Everything outside CSS — the
 * `theme-color` meta, the PWA manifest, email (#338), the docs site (#339) — reads
 * the hex values from here instead of retyping them.
 *
 * Kept import-free and to erasable TypeScript syntax, like logo.ts, so Node scripts
 * can load it with built-in type stripping.
 */

/** The three colours of the `m_` mark. */
export const BRAND = {
  navy: '#1A368B',
  cyan: '#13ECF3',
  ice: '#B8E6FE',
} as const;

/** Navy-tinted neutral ramp, for surfaces outside daisyUI (docs greys, email, OG). */
export const INK = {
  50: '#F4F7FC',
  100: '#E6EBF6',
  200: '#CDD6E9',
  300: '#A8B4D0',
  400: '#7C8CAE',
  500: '#57688F',
  600: '#384971',
  700: '#1F2E54',
  800: '#131F40',
  900: '#0A142F',
  950: '#040B1F',
} as const;

export type ThemeColorToken =
  | 'base-100'
  | 'base-200'
  | 'base-300'
  | 'base-content'
  | 'primary'
  | 'primary-content'
  | 'secondary'
  | 'secondary-content'
  | 'accent'
  | 'accent-content'
  | 'neutral'
  | 'neutral-content'
  | 'info'
  | 'info-content'
  | 'success'
  | 'success-content'
  | 'warning'
  | 'warning-content'
  | 'error'
  | 'error-content'
  | 'syntax-key'
  | 'syntax-string'
  | 'syntax-number'
  | 'syntax-boolean'
  | 'syntax-null'
  | 'syntax-punct'
  | 'method-get'
  | 'method-post'
  | 'method-put'
  | 'method-patch'
  | 'method-delete'
  | 'method-head'
  | 'method-options'
  | 'status-1xx'
  | 'status-2xx'
  | 'status-3xx'
  | 'status-4xx'
  | 'status-5xx';

export interface Theme {
  colorScheme: 'dark' | 'light';
  /** daisyUI `--color-*` tokens plus mockd's `syntax-*`, `method-*` and `status-*` tokens, as uppercase hex. */
  colors: Record<ThemeColorToken, string>;
}

const DARK_INK = '#081025';

export const THEMES = {
  /** What `prefers-color-scheme: dark` gets (daisyUI `prefersdark`). */
  'mockd-dark': {
    colorScheme: 'dark',
    colors: {
      'base-100': '#0C1731',
      'base-200': '#071027',
      'base-300': '#040A1E',
      'base-content': '#DBEFF9',
      primary: BRAND.cyan,
      'primary-content': DARK_INK,
      secondary: '#7BA4F0',
      'secondary-content': DARK_INK,
      accent: BRAND.ice,
      'accent-content': DARK_INK,
      neutral: '#020513',
      'neutral-content': '#DBEFF9',
      info: '#6BBAF0',
      'info-content': DARK_INK,
      success: '#52DB9C',
      'success-content': DARK_INK,
      warning: '#FAC053',
      'warning-content': DARK_INK,
      error: '#F97676',
      'error-content': DARK_INK,
      'syntax-key': '#87C6F2',
      'syntax-string': '#79DD9F',
      'syntax-number': '#FDBC6F',
      'syntax-boolean': '#BE9DF7',
      'syntax-null': '#9CA5B8',
      'syntax-punct': '#9CA5B8',
      'method-get': '#40E5E5',
      'method-post': '#61DA92',
      'method-put': '#FCB953',
      'method-patch': '#BC98F9',
      'method-delete': '#F97676',
      'method-head': '#A5B1CB',
      'method-options': '#EB8EC4',
      'status-1xx': '#A5B1CB',
      'status-2xx': '#61DA92',
      'status-3xx': '#79B6F4',
      'status-4xx': '#FCB953',
      'status-5xx': '#F97676',
    },
  },
  /** daisyUI `default`: applies whenever the OS isn't asking for dark. */
  'mockd-light': {
    colorScheme: 'light',
    colors: {
      'base-100': '#FFFFFF',
      'base-200': INK[50],
      'base-300': INK[100],
      'base-content': '#0B183B',
      primary: BRAND.navy,
      'primary-content': '#FFFFFF',
      secondary: '#285CC2',
      'secondary-content': '#FFFFFF',
      accent: '#00787D',
      'accent-content': '#FFFFFF',
      neutral: INK[900],
      'neutral-content': '#DBEFF9',
      info: '#0065B4',
      'info-content': '#FFFFFF',
      success: '#007748',
      'success-content': '#FFFFFF',
      warning: '#975105',
      'warning-content': '#FFFFFF',
      error: '#BE222A',
      'error-content': '#FFFFFF',
      'syntax-key': '#224FA7',
      'syntax-string': '#00713E',
      'syntax-number': '#A05000',
      'syntax-boolean': '#6F3BB2',
      'syntax-null': '#5A6378',
      'syntax-punct': '#5A6378',
      'method-get': '#00717C',
      'method-post': '#0B7643',
      'method-put': '#975105',
      'method-patch': '#7541B8',
      'method-delete': '#BE222A',
      'method-head': '#58637B',
      'method-options': '#A82F74',
      'status-1xx': '#58637B',
      'status-2xx': '#0B7643',
      'status-3xx': '#1762B6',
      'status-4xx': '#975105',
      'status-5xx': '#BE222A',
    },
  },
} as const satisfies Record<string, Theme>;

export type ThemeName = keyof typeof THEMES;

/** Shape and depth tokens, identical in both themes (docs/brand.md → Shape and depth). */
export const SHAPE = {
  '--radius-selector': '0.25rem',
  '--radius-field': '0.375rem',
  '--radius-box': '0.5rem',
  '--size-selector': '0.25rem',
  '--size-field': '0.25rem',
  '--border': '1px',
  '--depth': '0',
  '--noise': '0',
} as const;

/** `<meta name="theme-color">` and PWA manifest colours: each theme's page surface. */
export const THEME_COLOR = {
  dark: THEMES['mockd-dark'].colors['base-100'],
  light: THEMES['mockd-light'].colors['base-100'],
} as const;
